'use strict';

var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var Dropdown = require('../../Dropdown/src/Dropdown.js');
var TextInput = require('../../TextInput/src/TextInput.js');
var Select_module = require('../styles/Select.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description The Select component allow user to select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Select placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
const Select = React__namespace.forwardRef((props, ref) => {
    const { autoFocus, defaultValue, value, mode, options, invalid, disabled, readOnly, icon, leftIcon, placement = 'bottom-start', fixedWidth = true, className, menuClassName, withCrossIcon = true, optionsSort = true, flip = true, mobile, fullscreen, onChange, onFocus, onBlur, ...restProps } = props;
    const cx = reactUtils.useStyles(Select_module.default);
    const classNames = cx(className, 'container');
    const inputRef = React__namespace.useRef(null);
    const menuRef = React__namespace.useRef(null);
    const formControlContext = reactUtils.useFormControlContext();
    const [visible, setVisible] = reactUtils.useBoolean(false);
    const [focused, setFocused] = reactUtils.useBoolean(false);
    const [innerValue, setInnerValue] = reactUtils.useStateCallback(value ?? defaultValue);
    const [innerOptions, setInnerOptions] = React__namespace.useState(options || []);
    const isMultiple = React__namespace.useMemo(() => mode === 'multiple', [mode]);
    const optionsMap = React__namespace.useMemo(() => {
        if (!innerOptions) {
            return {};
        }
        return innerOptions.reduce((acc, o) => {
            acc[o.value] = o;
            return acc;
        }, {});
    }, [innerOptions]);
    const displayValue = React__namespace.useMemo(() => {
        if (innerValue === undefined || innerValue === null) {
            return undefined;
        }
        if (isMultiple && Array.isArray(innerValue)) {
            if (innerValue.length === 0) {
                return undefined;
            }
            if (innerOptions) {
                return innerValue
                    .map((v) => optionsMap[v]?.label)
                    .filter(Boolean)
                    .join(', ');
            }
        }
        return optionsMap[innerValue]?.label;
    }, [innerOptions, isMultiple, innerValue, optionsMap]);
    const setValue = React__namespace.useCallback((value) => {
        setInnerValue(value, (val) => {
            onChange?.(val ?? '');
        });
    }, [onChange, setInnerValue]);
    const hideMenu = React__namespace.useCallback(() => {
        setVisible.off();
        inputRef.current?.focus();
    }, [setVisible]);
    const handleSelect = React__namespace.useCallback((value) => {
        setValue(value);
        isMultiple ? inputRef.current?.focus() : hideMenu();
    }, [isMultiple, setValue, hideMenu]);
    const handleFocus = React__namespace.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React__namespace.useCallback((event) => {
        if (menuRef.current && event.relatedTarget === menuRef.current) {
            return;
        }
        setVisible.off();
        setFocused.off();
        onBlur?.(event);
    }, [setVisible, setFocused, onBlur]);
    const handleCrossIconClick = React__namespace.useCallback(() => setValue(''), [setValue]);
    const handleClose = React__namespace.useCallback(() => {
        setVisible.off();
        mobile && inputRef.current?.blur();
    }, [setVisible, mobile]);
    const iconElement = React__namespace.useMemo(() => {
        if (icon) {
            return icon;
        }
        if (withCrossIcon && visible && innerValue !== undefined) {
            return React__namespace.createElement(reactIcons.Cross, { className: cx('icon'), onClick: handleCrossIconClick });
        }
        return (React__namespace.createElement(reactIcons.ArrowDown, { className: cx('icon', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'is-arrow-turned': visible,
            }) }));
    }, [icon, withCrossIcon, visible, innerValue, cx, handleCrossIconClick]);
    React__namespace.useEffect(() => {
        if (value !== undefined) {
            setInnerValue(value);
        }
    }, [setInnerValue, value]);
    React__namespace.useEffect(() => {
        setInnerOptions(options);
    }, [options]);
    // autoFocus should be applied only once
    React__namespace.useEffect(() => {
        autoFocus && inputRef.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React__namespace.createElement(Dropdown.Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, className: menuClassName, mode: mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixedWidth, mobile: mobile, flip: flip, fullscreen: fullscreen, tabIndex: -1, optionsSort: optionsSort, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect },
        React__namespace.createElement("div", { "data-qa": "Select", className: classNames, tabIndex: -1 },
            React__namespace.createElement(TextInput.TextInput, { ...restProps, ref: reactUtils.useMergedRef(ref, inputRef), readOnly: true, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, value: displayValue, icon: iconElement, leftIcon: leftIcon, onFocus: handleFocus, onBlur: handleBlur }))));
});
Select.defaultProps = {
    withCrossIcon: true,
    fixedWidth: true,
    placement: 'bottom-start',
    optionsSort: true,
};
Select.displayName = 'Select';

exports.Select = Select;
//# sourceMappingURL=Select.js.map
